import {ArrayUtils, DateUtils} from '@wallaby-ui/core';
import {ChartColor, getAreaChartItems, getLineChartItems, LightChartTrace, useFormatters} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {sortBy} from 'lodash';
import {useMemo} from 'react';

import {ChartTypeType, ChartValueType, InterventionsDepartmentReportDto, ReportType} from './departmenttypes';
import {reportTypeToReportChartPropMap} from './useDepartmentReportChartCategories';

type ExtractChartsArray<T> = T extends {charts: infer U} ? U : never;
type ExtractChartType<T> = T extends {charts: (infer U)[]} ? U : never;

type StatisticsKeys = {
  [K in keyof InterventionsDepartmentReportDto]: K extends `${string}Statistics` ? K : never;
}[keyof InterventionsDepartmentReportDto];

type AllStatistics = InterventionsDepartmentReportDto[StatisticsKeys];

export type ChartDataArraySimple = ExtractChartsArray<AllStatistics>;
export type ChartDataRow = ExtractChartType<
  InterventionsDepartmentReportDto[keyof Pick<
    InterventionsDepartmentReportDto,
    {[K in keyof InterventionsDepartmentReportDto]: K extends `${string}Statistics` ? K : never}[keyof InterventionsDepartmentReportDto]
  >]
>;

export const useDepartmentReportChartData = (
  reportType: ReportType,
  data: InterventionsDepartmentReportDto | undefined,
  chartValue: ChartValueType | undefined,
  chartType: ChartTypeType | undefined,
  categories: ChartColor[]
): LightChartTrace[] => {
  const formatters = useFormatters();
  const visibleCategories = useMemo(() => categories.filter((c) => !c.hidden), [categories]);
  const unboundChartData = useMemo(() => {
    const chartData = data?.[reportTypeToReportChartPropMap[reportType]]?.charts ?? [];
    const sortedChartData = sortBy(chartData, (v: ChartDataRow) => v.month) as ChartDataRow[];
    return ArrayUtils.zipAll(
      ...visibleCategories.map((category) =>
        sortedChartData
          .filter((v: ChartDataRow) => {
            const dataCategory = `${v.category}`;
            const categoryId = category.id;
            if (
              (dataCategory === 'null' || dataCategory === 'undefined') &&
              (categoryId == null || categoryId === 'null' || categoryId === 'undefined')
            ) {
              return true;
            }
            return dataCategory === categoryId;
          })
          .map((v) => ({
            timestamp: DateUtils.fromISOFormat(v.month ?? '').toISOString(),
            count: chartValue === 'ABSOLUTE_COUNTS' ? v.measureCount ?? 0 : v.measureCountPer1000HospDays ?? 0,
            state: category.id,
          }))
      )
    );
  }, [visibleCategories, data, chartValue, reportType]);

  const lineChart = useMemo(() => {
    const {formatFloat} = formatters;
    return getLineChartItems(unboundChartData, visibleCategories, (v, i) => ({
      x: v[i]?.timestamp ?? '',
      y: v[i]?.count ?? 0,
      tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${visibleCategories[i].label}</b>: ${formatFloat(
        v[i]?.count ?? 0
      )}`,
    })).map((v) => ({...v, cliponaxis: false}));
  }, [formatters, unboundChartData, visibleCategories]);

  const areaChart = useMemo(() => {
    const {formatFloat} = formatters;
    return getAreaChartItems(
      unboundChartData,
      visibleCategories,
      (v, i) => ({
        x: v[i]?.timestamp ?? '',
        y: v[i]?.count ?? 0,
        tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${visibleCategories[i].label}</b>: ${formatFloat(
          v[i]?.count ?? 0
        )}`,
      }),
      true
    ).map((v) => ({...v, cliponaxis: false}));
  }, [unboundChartData, visibleCategories, formatters]);

  return chartType === 'LINE_CHART' ? lineChart : areaChart;
};
